import React, { useEffect, useMemo } from "react";
import { PayeeOverview } from "@app.automotus.io/components/PayeeOverview";
import Container from "@mui/material/Container";
import {
  PAYEE_OVERVIEW_TABS_LABELS,
  PAYEE_OVERVIEW_TABS_VALUES_CITATIONS,
  PAYEE_OVERVIEW_TABS_VALUES_DIGITAL_WALLET_TRANSACTIONS,
  PAYEE_OVERVIEW_TABS_VALUES_INVOICES,
  PAYEE_OVERVIEW_TABS_VALUES_PARK_TRANSACTIONS,
  PayeeOverviewTabValue,
} from "@app.automotus.io/components/PayeeOverview/PayeeOverviewTabs";
import endOfMonth from "date-fns/endOfMonth";
import Box from "@mui/material/Box";
import { useQuery } from "@apollo/client";
import {
  GET_CITATIONS,
  GET_INVOICES,
  GetCitationsData,
  GetCitationsVars,
  GetInvoicesData,
  GetInvoicesVars,
} from "common/graphql";
import { getAdjustedOverviewSums, getOverviewAmount } from "@app.automotus.io/components/routes/dashboard/utils";
import { useUserProfile } from "@app.automotus.io/components/hooks";
import { usePendingInvoiceReviewCount } from "@curbsuite/routes/dashboard/invoices/usePendingInvoiceReviewCount";
import { useDashboardClock, useOverviewQuery } from "@curbsuite/routes/dashboard/useOverviewQuery";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { Theme } from "@mui/material/styles";
import { CurbPassInactive } from "../curbpass";
import { shouldShowInactiveCurbPassPage, getAccountFeatures } from "../../utils/userProfile";

/**
 * TEMPORARY floor for the Container below (scorecards + table area) so it doesn't collapse
 * below a usable size. Not a table height — DataTableContainer's own tables are height:"100%"
 * and have no fixed size of their own. 1000 was picked so the table lands near its pre-rework
 * size (876px) once this Container's fixed overhead (PayeeOverview's scorecards + padding) is
 * subtracted from it. Scoping this to md-and-up (xs: 0) was tried and made rows not render at
 * all on mobile — the flex-grow chain here doesn't reliably produce a usable height on its own
 * yet, so this floor is load-bearing on mobile too for now, at the cost of page scroll on short
 * viewports. Revisit with a real dynamic height once the PayeeOverview scorecards get
 * redesigned (AUT-4997) — see memory and greptile.json.
 */
const DASHBOARD_MIN_CONTENT_HEIGHT = 1000;

export const DashboardLayout: React.FC = () => {
  const now = useDashboardClock();
  const { userProfile } = useUserProfile();
  const pendingInvoiceReviewCount = usePendingInvoiceReviewCount();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { tabs, activeTab, loading: tabsLoading, overviewTitleOverride } = usePayeeTabs(pendingInvoiceReviewCount);

  const handleChangeTab = useMemo(
    () => (newTab: PayeeOverviewTabValue) => {
      if (newTab === "invoices") {
        navigate("/dashboard/invoices");
        return;
      } else if (newTab === "citations") {
        navigate("/dashboard/citations");
        return;
      } else {
        if (activeTab === "park_transactions" || activeTab === "digital_wallet_transactions") {
          searchParams.set("tab", newTab);
          searchParams.set("page", "1");
          searchParams.delete("sort");
          setSearchParams(searchParams, { replace: true });
          return;
        }

        navigate(`/dashboard?tab=${newTab}`);
        return;
      }
    },
    [navigate, searchParams, setSearchParams, activeTab],
  );

  useEffect(() => {
    // Navigate to a valid tab if the active tab is not valid
    if (tabs && !tabs.some((tab) => tab.value === activeTab)) {
      handleChangeTab(tabs[0].value);
    }
  }, [handleChangeTab, tabs, activeTab]);

  const { isBusStation, isAirportTripCharges } = getAccountFeatures(userProfile);
  const isDemoUser = !!(userProfile?.email || "").match(/\+demo@automotus.co/);

  const dateParam = searchParams.get("date");
  let overviewDate = now;
  if (dateParam) {
    const selectedEnd = endOfMonth(new Date(dateParam));
    // Use end-of-month for past months, but cap at "now" for current month
    overviewDate = selectedEnd < now ? selectedEnd : now;
  }

  const isViewingPastMonth = dateParam ? endOfMonth(new Date(dateParam)) < now : false;

  const {
    data: overviewData,
    loading: overviewLoading,
    error: overviewError,
    refetch: refetchOverview,
  } = useOverviewQuery(overviewDate);

  const {
    data: invoicesData,
    loading: invoicesLoading,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useQuery<GetInvoicesData, GetInvoicesVars>(GET_INVOICES, {
    variables: {
      limit: 31,
      offset: 0,
      orderBy: [{ notice_sent_at: "desc" }],
    },
  });

  const {
    data: citationsData,
    loading: citationsLoading,
    error: citationsError,
    refetch: refetchCitations,
  } = useQuery<GetCitationsData, GetCitationsVars>(GET_CITATIONS, {
    variables: {
      limit: 31,
      offset: 0,
      orderBy: [{ notice_sent_at: "desc" }],
    },
  });

  if (shouldShowInactiveCurbPassPage(userProfile)) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          bgcolor: (theme: Theme) => theme.palette.grey[100],
        }}
      >
        <CurbPassInactive />
      </Box>
    );
  }

  // Period sums with adjustments (chargebacks/credits, AUT-6163) netted into the bucket of the
  // charge each one adjusts, signed so a net reversal shows as a negative total.
  const curPeriodSums = getAdjustedOverviewSums(
    overviewData?.curPeriodAggregate,
    overviewData?.curPeriodParkAdjustmentsAggregate,
    overviewData?.curPeriodGatewayAdjustmentsAggregate,
  );
  const prevPeriodSums = getAdjustedOverviewSums(
    overviewData?.prevPeriodAggregate,
    overviewData?.prevPeriodParkAdjustmentsAggregate,
    overviewData?.prevPeriodGatewayAdjustmentsAggregate,
  );

  const overviewSumKeyName =
    activeTab === "digital_wallet_transactions"
      ? "walletTransactionsAmount"
      : isAirportTripCharges && activeTab === "park_transactions"
      ? "tripChargeAmount"
      : isDemoUser
      ? "potentialRevenueAmount"
      : "registeredTransactionsAmount";

  return (
    <Box
      sx={{
        bgcolor: (theme: Theme) => theme.palette.grey[100],
        pt: 11,
        // display:flex + height:100% + flexGrow on this Box and the Container below — mirrors
        // PermitsLayout's wrapper — so Dashboard/Invoices/Citations (all rendered via this
        // layout's Outlet) get a definite, fill-the-viewport height chain down to their
        // DataTableContainer, instead of shrinking to their own content's natural height.
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flexGrow: 1,
      }}
    >
      <PayeeOverview
        tab={activeTab}
        tabs={tabs}
        overviewTitle={overviewTitleOverride}
        error={Boolean(overviewError || invoicesError || citationsError)}
        onClickTryAgain={() => {
          refetchOverview();
          refetchInvoices();
          refetchCitations();
        }}
        onChangeTab={handleChangeTab}
        loading={overviewLoading || invoicesLoading || citationsLoading || tabsLoading}
        displayPercentageChange={isViewingPastMonth ? false : undefined}
        percentageChangeSinceLastCycle={(() => {
          const curAmount = getOverviewAmount(curPeriodSums, overviewSumKeyName, { isBusStation });
          const prevAmount = getOverviewAmount(prevPeriodSums, overviewSumKeyName, { isBusStation });
          return Math.abs(Math.round((100 * (curAmount - prevAmount)) / (Math.abs(prevAmount) || 1)));
        })()}
        amount={
          activeTab === "invoices"
            ? invoicesData?.invoicesAggregate.aggregate.sum.amountPaid || 0
            : activeTab === "citations"
            ? citationsData?.citationsAggregate.aggregate.sum.paidAmount || 0
            : getOverviewAmount(curPeriodSums, overviewSumKeyName, { isBusStation })
        }
      />
      <Container
        maxWidth="xl"
        sx={{
          display: "flex",
          flexDirection: "column",
          py: 3,
          height: "100%",
          flexGrow: 1,
          minHeight: DASHBOARD_MIN_CONTENT_HEIGHT,
        }}
      >
        <Outlet />
      </Container>
    </Box>
  );
};

const usePayeeTabs = (pendingInvoiceReviewCount?: number) => {
  const { userProfile, loading } = useUserProfile();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const { invoicesEnabled, citationsEnabled, parkTransactionsEnabled, digitalWalletTransactionsEnabled } =
    getAccountFeatures(userProfile);

  // Airports close the month on this tab rather than topping up a wallet, so it reads as
  // "Monthly Charges" for them. The tab *value* stays digital_wallet_transactions -- it is a URL
  // param and part of PayeeOverviewTabValue -- so only the label changes.
  const { isAirportTripCharges } = getAccountFeatures(userProfile);
  const walletTabLabel = isAirportTripCharges
    ? "Monthly Charges"
    : PAYEE_OVERVIEW_TABS_LABELS[PAYEE_OVERVIEW_TABS_VALUES_DIGITAL_WALLET_TRANSACTIONS];

  const tabs: { value: PayeeOverviewTabValue; label: string; badge?: number }[] = useMemo(
    () => [
      ...(digitalWalletTransactionsEnabled
        ? [
            {
              value: PAYEE_OVERVIEW_TABS_VALUES_DIGITAL_WALLET_TRANSACTIONS,
              label: walletTabLabel,
            } as const,
          ]
        : []),
      ...(parkTransactionsEnabled
        ? [
            {
              value: PAYEE_OVERVIEW_TABS_VALUES_PARK_TRANSACTIONS,
              label: PAYEE_OVERVIEW_TABS_LABELS[PAYEE_OVERVIEW_TABS_VALUES_PARK_TRANSACTIONS],
            } as const,
          ]
        : []),
      ...(invoicesEnabled
        ? [
            {
              value: PAYEE_OVERVIEW_TABS_VALUES_INVOICES as PayeeOverviewTabValue,
              label: PAYEE_OVERVIEW_TABS_LABELS[PAYEE_OVERVIEW_TABS_VALUES_INVOICES],
              badge: pendingInvoiceReviewCount,
            },
          ]
        : []),
      ...(citationsEnabled
        ? [
            {
              value: PAYEE_OVERVIEW_TABS_VALUES_CITATIONS,
              label: PAYEE_OVERVIEW_TABS_LABELS[PAYEE_OVERVIEW_TABS_VALUES_CITATIONS],
            } as const,
          ]
        : []),
    ],
    [
      walletTabLabel,
      digitalWalletTransactionsEnabled,
      parkTransactionsEnabled,
      invoicesEnabled,
      citationsEnabled,
      pendingInvoiceReviewCount,
    ],
  );

  const isInvoicesTab = location.pathname.includes("/invoices");
  const isCitationsTab = location.pathname.includes("/citations");
  const tabParam = searchParams.get("tab");
  const defaultTab = tabs.length > 0 ? tabs[0].value : "digital_wallet_transactions";
  const activeTab = (
    isInvoicesTab ? "invoices" : isCitationsTab ? "citations" : tabParam || defaultTab
  ) as PayeeOverviewTabValue;

  // Only the airport wallet tab overrides its heading. Deriving it generically from the active
  // tab's label would silently rename the invoices heading ("Automated Invoices Overview").
  const overviewTitleOverride =
    isAirportTripCharges && activeTab === "digital_wallet_transactions" ? walletTabLabel : undefined;

  return { tabs: loading ? undefined : tabs, activeTab, loading, overviewTitleOverride };
};
